import i18n from 'i18next';

import type { INavItem } from '@/types/navigation';

import routes from '@/config/routes';
import { UserType } from '@/store/enums';
import { canAccessCampaignsNav, canAccessResourcesNav, hasPermission } from '@/helpers/permissionValidator';
import { Permission, PermissionLevel } from '@/constants/client/permission';
import { store } from '@/store';
import { IFeatureFlag } from '@/store/slices/featureFlags/types';

const AGENT_NAV_ITEMS = (hasActiveGigs: boolean, featureFlags: IFeatureFlag): INavItem[] => {
  const { isCommunityEnabled } = featureFlags;

  const result: INavItem[] = isCommunityEnabled
    ? [
        {
          to: routes.sp.community,
          text: i18n.t`navbar.agent.community`,
        },
      ]
    : [];

  result.push(
    {
      to: routes.sp.onboarding.gigs,
      text: i18n.t`navbar.agent.marketplace`,
    },
    {
      to: routes.sp.onboarding.appliedGigs,
      text: i18n.t`navbar.agent.appliedGigs`,
    },
  );

  if (hasActiveGigs) {
    result.push(
      {
        to: routes.sp.gigsMenu.gigs,
        text: i18n.t`navbar.agent.gigsMenu.gigs`,
      },
      {
        to: routes.sp.schedulesMenu.schedules,
        text: i18n.t`navbar.agent.schedules`,
      },
      {
        to: routes.sp.communicationsMenu.communications,
        text: i18n.t`navbar.agent.communications`,
      },
      {
        to: routes.sp.learn,
        text: i18n.t`navbar.agent.learn`,
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      {
        to: routes.sp.scheduling.resources.index,
        text: i18n.t`navbar.agent.resources`,
      },
    );
  }

  return result;
};

const GIGS_MENU_ITEMS = (): INavItem[] => [
  {
    to: routes.sp.gigsMenu.gigCompliance,
    text: i18n.t`navbar.agent.gigsMenu.gigCompliance`,
  },
  {
    to: routes.sp.gigsMenu.myGigs,
    text: i18n.t`navbar.agent.gigsMenu.myGigs`,
  },
];

const SCHEDULES_MENU_ITEMS = (): INavItem[] => [
  {
    to: routes.sp.schedulesMenu.mySchedule,
    text: i18n.t`navbar.agent.schedulesMenu.mySchedule`,
  },
  {
    to: routes.sp.schedulesMenu.myAvialability,
    text: i18n.t`navbar.agent.schedulesMenu.myAvailability`,
  },
  {
    to: routes.sp.schedulesMenu.timeOff,
    text: i18n.t`navbar.agent.schedulesMenu.timeOff`,
  },
];

const COMMUNICATIONS_MENU_ITEMS = (userType: UserType): INavItem[] => {
  const result: INavItem[] = [
    {
      to: routes.sp.communicationsMenu.mail,
      text: i18n.t`navbar.agent.communicationsMenu.mail`,
    },
    {
      to: routes.sp.communicationsMenu.chat,
      text: i18n.t`navbar.agent.communicationsMenu.chat`,
      target: '_blank',
    },
  ];
  if (userType === UserType.client) {
    result.push({
      to: routes.client.communications.chatLog,
      text: i18n.t`navbar.client.suChatLog`,
    });
  }
  if (userType === UserType.client && hasPermission(Permission.COMMUNICATIONS, PermissionLevel.ANY)) {
    result.push({
      to: routes.client.communications.notify,
      text: i18n.t`navbar.client.suNotify`,
    });
  }
  result.push(
    {
      to: routes.sp.communicationsMenu.serviceRequest,
      text: i18n.t`navbar.agent.communicationsMenu.serviceRequest`,
      target: '_blank',
    },
    {
      to: routes.sp.communicationsMenu.remoteSupport,
      text: i18n.t`navbar.agent.communicationsMenu.remoteSupport`,
      target: '_blank',
    },
  );

  return result;
};

const CLIENT_NAV_ITEMS = (): INavItem[] => {
  const isCommunityEnabled = store.getState().featureFlags.featureFlags.isCommunityEnabled;
  const result: INavItem[] = [];

  if (isCommunityEnabled) {
    result.push({
      to: routes.sp.community,
      text: i18n.t`navbar.agent.community`,
    });
  }

  if (canAccessCampaignsNav() || hasPermission(Permission.TRAINING_CLASSES, PermissionLevel.ANY)) {
    result.push({
      to: routes.client.onboardingMenu.onboarding,
      text: i18n.t`navbar.client.onboarding`,
    });
  }

  if (hasPermission(Permission.STAFFING, PermissionLevel.ANY)) {
    result.push({
      to: routes.client.contracts.index,
      text: i18n.t`navbar.client.gigs`,
    });
  }

  if (hasPermission(Permission.SCHEDULE, PermissionLevel.ANY)) {
    result.push({
      to: routes.client.scheduling.index,
      text: i18n.t`navbar.client.btnScheduling`,
    });
  }

  if (canAccessResourcesNav()) {
    result.push({
      to: routes.client.resources.index,
      text: i18n.t`navbar.client.btnResources`,
    });
  }

  if (hasPermission(Permission.COMMUNICATIONS, PermissionLevel.ANY)) {
    result.push({
      to: routes.sp.communicationsMenu.communications,
      text: i18n.t`navbar.agent.communications`,
    });
  }

  if (hasPermission(Permission.REPORTING, PermissionLevel.ANY)) {
    result.push({
      to: routes.client.reports.index,
      text: i18n.t`navbar.client.btnReports`,
    });
  }

  return result;
};

const ONBOARDING_MENU_ITEMS = (): INavItem[] => {
  const result: INavItem[] = [];

  if (canAccessCampaignsNav()) {
    result.push({
      to: routes.client.campaigns.index,
      text: i18n.t`navbar.client.btnCampaigns`,
    });
  }

  if (hasPermission(Permission.TRAINING_CLASSES, PermissionLevel.ANY)) {
    result.push({
      to: routes.client.learn.index,
      text: i18n.t`navbar.client.btnLearn`,
    });
  }

  return result;
};

const ADMIN_NAV_ITEMS = (): INavItem[] => {
  const items: INavItem[] = [
    {
      to: routes.admin.course.list,
      text: i18n.t`navbar.admin.courseBuilder`,
    },
    {
      to: routes.admin.class.list,
      text: i18n.t`navbar.admin.classBuilder`,
    },
    {
      to: routes.admin.agents.index,
      text: i18n.t`navbar.admin.admin`,
    },
    {
      to: routes.admin.refer.index,
      text: i18n.t`navbar.admin.refer`,
    },
    {
      to: routes.admin.community.index,
      text: i18n.t`navbar.admin.community`,
    },
  ];

  if (hasPermission(Permission.PERMISSION, PermissionLevel.MODIFY)) {
    items.splice(2, 0, {
      to: routes.admin.permission.index,
      text: i18n.t`navbar.admin.permission`,
    });
  }

  items.unshift({
    to: routes.admin.gig.list,
    text: i18n.t`navbar.admin.gigs`,
  });
  return items;
};

const ADMIN_MENU_ITEMS = (): INavItem[] => [
  {
    to: routes.admin.agents.serviceProviders, //routes.admin.agents.serviceProvider,
    text: i18n.t`navbar.admin.adminMenu.serviceProviders`,
  },
  {
    to: routes.admin.agents.esignDocuments,
    text: i18n.t`navbar.admin.adminMenu.eSignDocuments`,
  },
  {
    to: routes.admin.agents.fraudReviewGroups,
    text: i18n.t`navbar.admin.fraudReviewGroups`,
  },
];

const REFER_MENU_ITEMS = (): INavItem[] => [
  {
    to: routes.admin.refer.payRates,
    text: i18n.t`navbar.admin.referMenu.payRates`,
  },
  {
    to: routes.admin.refer.featuredGigs,
    text: i18n.t`navbar.admin.referMenu.featuredGigs`,
  },
];

export {
  ADMIN_NAV_ITEMS,
  AGENT_NAV_ITEMS,
  CLIENT_NAV_ITEMS,
  COMMUNICATIONS_MENU_ITEMS,
  GIGS_MENU_ITEMS,
  SCHEDULES_MENU_ITEMS,
  ADMIN_MENU_ITEMS,
  REFER_MENU_ITEMS,
  ONBOARDING_MENU_ITEMS,
};
